import {
  missingDataMode,
  trimMissing,
  useChartAnimation,
  useChartLifecycle,
  type MissingDataMode,
} from "@nexus/widget-kit";
import { useContext, useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import { LegacyGridContainLabel } from "echarts/features";
import {
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import {
  defineLabels,
  firstError,
  MetricStat,
  resolveChartColors,
  resolveLabels,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetContentReadyContext,
  WidgetFullscreenContext,
  WidgetSkeleton,
  WidgetState,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { buildOption, formatCount, formatShare } from "./chart";
import { groupColors } from "./colors";
import {
  buildWeeklyModel,
  DEFAULT_LOW_SAMPLE_THRESHOLD,
  isMissingWeek,
  type WeeklyModel,
} from "./model";
import { NETWORK_ID_WEEKLY_CONTRACT } from "./types";
import type { NetworkIdWeeklyData } from "./types";

echarts.use([
  BarChart,
  GridComponent,
  LegendComponent,
  MarkAreaComponent,
  TooltipComponent,
  CanvasRenderer,
  LegacyGridContainLabel,
]);

export interface NetworkIdWeeklyConfig {
  title?: string;
  /** `true` draws the chart in; off by default (drawn at once). */
  animateChart?: boolean;
  labels?: Record<string, string>;
  /** A week with fewer detections is flagged as an unreliable distribution. */
  lowSampleThreshold?: number;
  /** "all" shows every week label; "auto" lets the chart skip labels when they would overlap. */
  weekLabels?: "all" | "auto";
  /** Show gap (default) keeps every week of the period; Fit to data crops the weeks without data at its ends. */
  missingData?: MissingDataMode;
}

export const LABELS = defineLabels([
  { key: "totalLabel", name: "Total: label", default: "Total detections" },
  {
    key: "distributionLabel",
    name: "Distribution: label",
    default: "{weeks} distribution",
    hint: '{weeks} is replaced by "1 week" or "N weeks", the number of weeks shown.',
  },
  { key: "weeklyMaxLabel", name: "Weekly max: label", default: "Max in a given week" },
  { key: "other", name: "Other groups", default: "Other" },
  { key: "unfinished", name: "Unfinished week", default: "unfinished" },
  { key: "lowSample", name: "Low sample (tooltip)", default: "Low sample", advanced: true },
  {
    key: "lowSampleHint",
    name: "Low sample explanation (tooltip)",
    default: "distribution not reliable",
    advanced: true,
  },
  { key: "total", name: "Total (tooltip)", default: "Detections", advanced: true },
  { key: "emptyTitle", name: "Empty title", default: "No detections" },
  {
    key: "emptyDescription",
    name: "Empty description",
    default: "No network ID detection in this period.",
  },
] as const);

type WeeklyLabels = ReturnType<typeof resolveLabels<typeof LABELS>>;

export const manifest: WidgetManifest = {
  id: "network-id-weekly",
  name: "Network ID Weekly",
  description: "Weekly share of network groups among network ID detections.",
  version: "1.0.0",
  labels: LABELS,
  dataRequests: {
    weekly: {
      source: "network-id-weekly",
      contract: NETWORK_ID_WEEKLY_CONTRACT,
      configurable: true,
      sources: ["network-id-weekly"],
    },
  },
};

export const defaultConfig: NetworkIdWeeklyConfig = {
  title: "Network ID",
  lowSampleThreshold: DEFAULT_LOW_SAMPLE_THRESHOLD,
  weekLabels: "all",
};

/**
 * A headline figure listing one line per network group, in the group's colour.
 * Narrow widgets read the groups on one wrapping line; from `@lg/widget` each
 * group takes its own line.
 */
function GroupStat({
  label,
  rows,
  className = "",
}: {
  label: string;
  rows: { key: string; text: string; color?: string; detail?: string | null }[];
  className?: string;
}) {
  return (
    <div className={`min-w-0 ${className}`}>
      <dt className="text-metric-caption font-medium text-secondary">{label}</dt>
      <dd className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 @lg/widget:flex-col @lg/widget:gap-x-0">
        {rows.length === 0 ? (
          <span className="text-metric-label font-semibold text-muted">—</span>
        ) : (
          rows.map((row) => (
            <span
              key={row.key}
              className={`text-metric-label font-semibold whitespace-nowrap tabular-nums ${row.color ? "" : "text-muted"}`}
              style={row.color ? { color: row.color } : undefined}
            >
              {row.text}
              {row.detail ? (
                <span className="ml-1.5 text-metric-detail font-normal text-muted">
                  {row.detail}
                </span>
              ) : null}
            </span>
          ))
        )}
      </dd>
    </div>
  );
}

/**
 * Total, distribution and weekly maximum. The layout follows the widget's own
 * width (container queries), never the screen: a narrow widget (three or more
 * per row on a laptop or a 720p wall) keeps the total beside the distribution
 * and the weekly maxima on one line under them, so the chart keeps its height;
 * from `@lg/widget` the three figures stand in three columns.
 */
function Headline({ model, labels }: { model: WeeklyModel; labels: WeeklyLabels }) {
  const palette = groupColors(model.groups, model.families);
  const distribution = model.top.map(({ group, share }) => ({
    key: group,
    text: `${group} ${formatShare(share)}`,
    color: palette.get(group)?.css,
  }));
  if (model.otherShare !== null)
    distribution.push({
      key: "\u0000other",
      text: `${labels.other} ${formatShare(model.otherShare)}`,
      color: undefined,
    });
  const measuredWeeks = model.weeks.filter((week) => !week.missing).length;
  return (
    <dl className="grid shrink-0 grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-5 gap-y-2 px-(--density-widget-body-padding) pt-(--density-widget-body-padding) @lg/widget:grid-cols-[0.85fr_1.075fr_1.075fr] @lg/widget:gap-x-6 @lg/widget:gap-y-3">
      <MetricStat label={labels.totalLabel} value={formatCount(model.total)} />
      <GroupStat
        label={labels.distributionLabel.replace(
          "{weeks}",
          `${measuredWeeks} ${measuredWeeks > 1 ? "weeks" : "week"}`,
        )}
        rows={distribution}
      />
      <GroupStat
        className="col-span-2 @lg/widget:col-span-1"
        label={labels.weeklyMaxLabel}
        rows={model.weeklyMax.map(({ group, share, week }) => ({
          key: group,
          text: `${group} ${share === null ? "—" : formatShare(share)}`,
          color: palette.get(group)?.css,
          detail: week,
        }))}
      />
    </dl>
  );
}

function NetworkIdWeeklyComponent({ data, config }: WidgetProps<NetworkIdWeeklyConfig>) {
  const { markContentReady } = useContext(WidgetContentReadyContext);
  const { fullscreen: inFocusOverlay } = useContext(WidgetFullscreenContext);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);
  const hasRenderedRef = useRef(false);

  const error = firstError(data, ["weekly"]);
  const weekly = unwrap<NetworkIdWeeklyData>(data.weekly);
  const isLoading = data.weekly?.isLoading ?? false;
  const title = config.title?.trim() || defaultConfig.title || manifest.name;
  const labelsJson = JSON.stringify(resolveLabels(LABELS, config));
  const labels = useMemo(() => JSON.parse(labelsJson) as WeeklyLabels, [labelsJson]);
  const threshold = config.lowSampleThreshold ?? DEFAULT_LOW_SAMPLE_THRESHOLD;
  const allWeekLabels = (config.weekLabels ?? defaultConfig.weekLabels) === "all";
  const missingData = missingDataMode(config.missingData);

  const model = useMemo(
    () =>
      weekly
        ? buildWeeklyModel(
            missingData === "fit"
              ? {
                  ...weekly,
                  weeks: trimMissing(weekly.weeks, (week) => isMissingWeek(week, weekly)),
                }
              : weekly,
            threshold,
          )
        : null,
    [weekly, threshold, missingData],
  );
  const isEmpty = !isLoading && !error && (!model || model.total === 0);
  const showChart = !error && !isEmpty && model !== null;
  const firstLoad = isLoading && !hasRenderedRef.current;

  // Entry animation only when the author asks for it ("Animate chart").
  const animate = useChartAnimation(config);
  const renderRef = useRef<(host: HTMLElement, chart: echarts.ECharts) => void>(() => {});
  useEffect(() => {
    renderRef.current = (host, chart) => {
      if (model)
        chart.setOption(
          buildOption(model, resolveChartColors(host), labels, {
            allWeekLabels,
            chartWidth: () => host.clientWidth,
            animate,
          }),
          true,
        );
    };
  }, [model, labels, allWeekLabels, animate]);

  useEffect(() => {
    const host = chartRef.current;
    if (!host || !showChart || isLoading) return;
    if (!chartInstanceRef.current || chartInstanceRef.current.isDisposed()) {
      chartInstanceRef.current = echarts.init(host, null, { renderer: "canvas" });
    }
    renderRef.current(host, chartInstanceRef.current);
    hasRenderedRef.current = true;
    markContentReady();
  }, [model, labels, allWeekLabels, showChart, isLoading, markContentReady]);

  useChartLifecycle(chartRef, chartInstanceRef, (host, chart) => {
    if (hasRenderedRef.current) renderRef.current(host, chart);
  });

  return (
    <Widget
      title={title}
      intrinsicHeight={!inFocusOverlay}
      actions={isLoading && !firstLoad ? <UpdatingPill /> : undefined}
    >
      <div
        className={`flex w-full flex-col ${inFocusOverlay ? "h-full min-h-0 overflow-y-auto" : "flex-1"}`}
      >
        {model && !error && <Headline model={model} labels={labels} />}
        <div className="relative min-h-(--widget-chart-min-height) w-full flex-1">
          <div
            ref={chartRef}
            role="img"
            aria-label={
              model
                ? `Weekly share of network groups over ${model.weeks.length} weeks, ${formatCount(model.total)} detections.`
                : "Weekly network group chart"
            }
            className="absolute inset-0 h-full w-full transition-opacity duration-200"
            style={{ opacity: showChart ? 1 : 0 }}
          />
          {error && <WidgetIssueState error={error} className="absolute inset-0" />}
          {isEmpty && (
            <WidgetState
              title={labels.emptyTitle}
              description={labels.emptyDescription}
              className="absolute inset-0"
            />
          )}
          {firstLoad && <WidgetSkeleton shape="chart" className="absolute inset-0" />}
        </div>
      </div>
    </Widget>
  );
}

export const Component = NetworkIdWeeklyComponent;

const widgetModule: WidgetModule<NetworkIdWeeklyConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
